import instance from "../store/actions/instance";
import {
  normalizePaymentStatus,
  normalizeTrackingStatus,
} from "./domain/trackingStatus";
import {
  normalizeDisplayCurrency,
  normalizeMoneyValue,
} from "./domain/money";

const trackingPath = (base, trackingId) =>
  `${base}/${encodeURIComponent(`${trackingId || ""}`.trim())}/`;

export const CHECKOUT_V2_API_PATHS = Object.freeze({
  validateCartContext: "/validate_cart_context/",
  checkoutQuote: "/checkout_quote/",
  paymentStatus: (trackingId) => trackingPath("/payment_status", trackingId),
  paymentRetry: (trackingId) => trackingPath("/payment_retry", trackingId),
  placeOrder: "/promise_place/",
  legacyTracking: (trackingId) => trackingPath("/track_order_new", trackingId),
  deliveryTime: "/get_delivery_time/",
  emailAuth: "/email-auth/",
  phoneAuth: "/phone-auth/",
  verifyEmail: "/verify-email/",
  verifyPhone: "/verify-phone/",
  resetEmailPassword: "/forgot-password/",
  resetPhonePassword: "/forgot-password-phone/",
});

const finiteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const timestamp = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (Number.isFinite(numeric))
    return numeric > 0 && numeric < 100000000000 ? numeric * 1000 : numeric;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const safeText = (value, maxLength = 200) =>
  `${value === null || value === undefined ? "" : value}`
    .trim()
    .slice(0, maxLength);

const normalizeShopperSafeErrors = (...sources) =>
  sources
    .flatMap((source) =>
      Array.isArray(source) ? source : source ? [source] : [],
    )
    .map((error) => {
      if (error && typeof error === "object") {
        const code = safeText(error.code || error.reason, 120);
        const message = safeText(error.message, 240);
        return code || message
          ? { code: code || "checkout_unavailable", message: message || null }
          : null;
      }
      const code = safeText(error, 120);
      return code ? { code, message: null } : null;
    })
    .filter(Boolean)
    .slice(0, 20);

const enabled = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const unwrapEnvelope = (response = {}) => {
  const envelope = response || {};
  const data =
    envelope.data && typeof envelope.data === "object"
      ? envelope.data
      : envelope;
  return { envelope, data };
};

const normalizeServerLine = (line = {}, index = 0) => {
  const quote = line.price_quote || line.priceQuote || {};
  const unitPrice = normalizeMoneyValue(
    quote.unit_price_money ?? quote.unitPriceMoney ?? quote.unit_price ?? quote.unitPrice,
  );
  const lineTotal = normalizeMoneyValue(
    quote.line_total_money ?? quote.lineTotalMoney ?? quote.line_total ?? quote.lineTotal,
  );
  return {
    lineId: safeText(
      line.lineId ||
        line.line_id ||
        line.cartLineId ||
        line.cart_line_id ||
        `${line.id || line.product_id || "line"}:${index}`,
      120,
    ),
    productId: line.productId || line.product_id || line.product || line.id,
    quantity: finiteNumber(
      line.quantity ??
        line.adjustedQuantity ??
        line.adjusted_quantity ??
        line.effective_quantity,
    ),
    originalQuantity: finiteNumber(
      line.originalQuantity || line.original_quantity || line.requested_quantity,
    ),
    reason: safeText(
      line.reason && typeof line.reason === "object"
        ? line.reason.code || line.reason.message
        : line.reason,
      200,
    ),
    restoreAllowed: enabled(
      line.restoreAllowed ?? line.restore_allowed ?? line.can_restore,
    ),
    priceQuote:
      unitPrice !== null || lineTotal !== null ? { unitPrice, lineTotal } : null,
  };
};

export const encodeCheckoutServerRequest = ({
  context = {},
  lines = [],
  draft = {},
  contact = {},
  promise = {},
  promotions = {},
  currencyCode = null,
  quoteId = null,
  restoreLineId = null,
} = {}) => {
  const promo = promotions.promo || promotions.promos || {};
  const discountIds = (Array.isArray(promotions.discounts)
    ? promotions.discounts
    : Object.values(promotions.discounts || {}).flat()
  )
    .map((item) => (typeof item === "object" ? item.id : item))
    .filter((value) => value !== null && value !== undefined)
    .slice(0, 100);
  const smartPromoIds = (
    Array.isArray(promotions.smartPromos || promotions.smart_promos)
      ? promotions.smartPromos || promotions.smart_promos
      : []
  )
    .map((item) => (typeof item === "object" ? item.id : item))
    .filter((value) => value !== null && value !== undefined)
    .slice(0, 100);
  return {
  // Carrier quoting still consumes the legacy top-level address contract.
  // Keep these fields additive while the normalized context owns the form.
  block: safeText(context.address?.block, 200),
  street: safeText(context.address?.street, 200),
  avenue: safeText(context.address?.avenue, 200),
  building: safeText(context.address?.building, 200),
  floor: safeText(context.address?.floor, 200),
  apartment: safeText(context.address?.apartment, 200),
  context: {
    fulfillment_method: context.type,
    branch_id: context.branchId,
    branch_delivery_charge_id: context.branchDeliveryChargeId,
    country_id: context.countryId,
    area_id: context.areaId,
    lat: context.address?.lat ?? null,
    lng: context.address?.lng ?? null,
    gps_confirmed: context.address?.gpsConfirmed === true,
    pickup_mode: context.pickupMode,
    car_make: context.vehicle?.make || null,
    car_color: context.vehicle?.color || null,
    car_plate_number: context.vehicle?.plateNumber || null,
    table_token: context.qr?.tableToken || null,
    parking_number: context.qr?.parkingNumber || null,
    dine_in_order_id: context.qr?.dineInOrderId || null,
    dine_in_session_id: context.qr?.dineInSessionId || null,
    expected_delivery_date: context.time?.date || null,
    expected_delivery_slot_start: context.time?.start || null,
    expected_delivery_slot_end: context.time?.end || null,
    is_asap: context.time?.type === "asap",
    asap_authority:
      context.time?.type === "asap" ? context.time?.authority || null : null,
    fingerprint: context.fingerprint || null,
  },
  cart: lines.map((line) => ({
    line_id: line.lineId,
    id: line.productId,
    quantity: line.quantity,
    options: (line.options || []).map((option) => ({
      id: option.id,
      quantity: option.quantity,
      quantity_exclusive: option.quantityExclusive === true,
    })),
    extra_fields: (line.extraFields || []).map((field) => ({
      id: field.id,
      value: field.value,
    })),
    type_of_product: line.typeOfProduct || null,
    allow_preordering: line.allowPreordering === true,
    is_preordered: line.isPreordered === true,
    booking_slot: line.bookingSlot || null,
    resource: line.resource || null,
    resource_slot: line.resourceSlot || null,
    components: (line.components || []).map((component) => ({
      id: component.id,
      quantity: component.quantity,
    })),
  })),
  extras: {
    no_cutlery: draft.noCutlery === true,
    is_gift: enabled(
      draft.isGift ??
        draft.is_gift ??
        promise.isGift ??
        promise.is_gift,
    ),
    gift_wrapping: draft.giftWrapping === true,
    use_customer_wallet: draft.useCustomerWallet === true,
    use_customer_points: draft.useCustomerPoints === true,
  },
  contact: {
    name: safeText(contact.name, 120),
    phone: safeText(contact.phone, 80),
    email: safeText(contact.email, 254).toLowerCase(),
  },
  payment_method: draft.selectedPaymentMethod || null,
  selected_country_code: currencyCode || null,
  promotions: {
    promotion_id:
      draft.promotionId || promo.id || promotions.promotionId || null,
    promotion_code: safeText(
      draft.promotionCode ||
        promo.code ||
        promo.promo_code ||
        promotions.promotionCode,
      120,
    ) || null,
    discount_ids: discountIds,
    smart_promo_ids: smartPromoIds,
  },
  quote_id: quoteId || undefined,
  restore_line_id: restoreLineId || undefined,
  };
};

export const normalizeContextValidationResponse = (
  response,
  now = Date.now(),
) => {
  const { envelope, data } = unwrapEnvelope(response);
  const retained = data.retained_items || data.retainedItems || [];
  const adjusted = data.adjusted_items || data.adjustedItems || [];
  const removed = data.removed_items || data.removedItems || [];
  const rawErrors = data.errors || envelope.errors || [];
  const errorList = (Array.isArray(rawErrors) ? rawErrors : [rawErrors]).filter(Boolean);
  const errorDetails = errorList.map(error => {
    if (typeof error !== "object") return { code: safeText(error, 240) };
    const message = typeof error.message === "string" ? safeText(error.message, 500) : null;
    return { code: safeText(error.code || message || "context_unavailable", 240), message,
      english: safeText(error.message?.english || error.english, 500),
      arabic: safeText(error.message?.arabic || error.arabic, 500) };
  }).filter(error => error.code);
  const errors = errorDetails.map(error => error.code).filter(Boolean);
  const normalizedRemoved = removed.map(normalizeServerLine);
  const normalizedRetained = retained.map((line, index) =>
    typeof line === "object"
      ? normalizeServerLine(line, index)
      : normalizeServerLine({ line_id: safeText(line, 120) }, index),
  );

  return {
    success: envelope.success !== false && data.success !== false,
    valid: data.valid === true,
    context: data.context || null,
    retainedLines: normalizedRetained,
    retainedLineIds: normalizedRetained
      .map((line) => line.lineId)
      .filter(Boolean),
    adjustedLines: adjusted.map(normalizeServerLine),
    removedLines: normalizedRemoved,
    removedLineIds: normalizedRemoved.map((line) => line.lineId),
    errors,
    errorDetails,
    pricingStatus:
      safeText(data.pricing_status || data.pricingStatus, 40) === "pending"
        ? "pending"
        : "ready",
    pendingReasons: (
      Array.isArray(data.pending_reasons || data.pendingReasons)
        ? data.pending_reasons || data.pendingReasons
        : []
    )
      .map((reason) => safeText(reason, 120))
      .filter(Boolean)
      .slice(0, 20),
    displayCurrency: normalizeDisplayCurrency(
      data.display_currency || data.displayCurrency,
    ),
    minimumOrderValue: normalizeMoneyValue(
      data.minimum_order_value_money ??
        data.minimumOrderValueMoney ??
        data.minimum_order_value ??
        data.minimumOrderValue,
    ),
    deliveryRate: normalizeMoneyValue(
      data.delivery_rate_money ??
        data.deliveryRateMoney ??
        data.delivery_rate ??
        data.deliveryRate,
    ),
    subtotal: normalizeMoneyValue(
      data.subtotal_money ?? data.subtotalMoney ?? data.subtotal,
    ),
    minimumOrderRemaining: normalizeMoneyValue(
      data.mov_remaining_money ??
        data.movRemainingMoney ??
        data.mov_remaining ??
        data.movRemaining,
    ),
    checkedAt: now,
  };
};

const quoteValue = (
  quote,
  snakeKey,
  camelKey,
  currency,
  fractionDigits,
) =>
  normalizeMoneyValue(
    quote[`${snakeKey}_money`] ??
      quote[`${camelKey}Money`] ??
      quote.money?.[snakeKey] ??
      quote.money?.[camelKey] ??
      quote[snakeKey] ??
      quote[camelKey],
    { currency, fractionDigits },
  );

export const normalizeCheckoutQuoteResponse = (
  response,
  contextFingerprint = null,
  now = Date.now(),
) => {
  const { envelope, data } = unwrapEnvelope(response);
  const quote = data.quote || envelope.quote || data;
  const currency =
    safeText(quote.currency || data.currency, 12).toUpperCase() || null;
  const fractionDigits = finiteNumber(
    quote.fraction_digits ?? quote.fractionDigits ?? data.fraction_digits,
  );
  const displayCurrency = normalizeDisplayCurrency(
    quote.display_currency ||
      quote.displayCurrency ||
      data.display_currency ||
      data.displayCurrency,
  );
  const paymentMethods =
    quote.payment_methods || quote.paymentMethods || data.payment_methods || [];
  const paymentMethodOptions = (
    Array.isArray(paymentMethods) ? paymentMethods : []
  )
    .map((method) => ({
      id: safeText(
        typeof method === "object" ? method.id || method.value : method,
        80,
      ),
      eligible:
        typeof method === "object" && method.eligible === false ? false : true,
      reason: safeText(
        typeof method === "object"
          ? method.reason && typeof method.reason === "object"
            ? method.reason.code || method.reason.message
            : method.reason
          : "",
        200,
      ) || null,
      label:
        safeText(
          typeof method === "object" ? method.label || method.name : "",
          120,
        ) || null,
      arLabel:
        safeText(
          typeof method === "object"
            ? method.ar_label || method.arLabel || method.label_ar
            : "",
          120,
        ) || null,
      type:
        safeText(typeof method === "object" ? method.type || method.kind : "", 40) ||
        null,
    }))
    .filter((method) => method.id)
    .slice(0, 50);
  return {
    success: envelope.success !== false && data.success !== false && !!quote,
    // Placement needs the second availability check too: stock can change
    // between validate_cart_context and checkout_quote.
    cartValidation: Array.isArray(data.retained_items) || Array.isArray(data.removed_items)
      ? {
        ...normalizeContextValidationResponse(response, now),
        subtotal: quoteValue(quote, "subtotal", "subtotal", currency, fractionDigits),
        deliveryRate: quoteValue(quote, "delivery_rate", "deliveryRate", currency, fractionDigits),
        minimumOrderValue: quoteValue(quote, "minimum_order", "minimumOrder", currency, fractionDigits),
        minimumOrderRemaining: quoteValue(quote, "mov_remaining", "movRemaining", currency, fractionDigits),
      }
      : null,
    pricingStatus:
      safeText(
        quote.pricing_status ||
          quote.pricingStatus ||
          data.pricing_status ||
          data.pricingStatus,
        40,
      ) === "pending"
        ? "pending"
        : "ready",
    pendingReasons: (
      Array.isArray(
        quote.pending_reasons ||
          quote.pendingReasons ||
          data.pending_reasons ||
          data.pendingReasons,
      )
        ? quote.pending_reasons ||
          quote.pendingReasons ||
          data.pending_reasons ||
          data.pendingReasons
        : []
    )
      .map((reason) => safeText(reason, 120))
      .filter(Boolean)
      .slice(0, 20),
    valid:
      data.valid === true ||
      (data.valid === undefined && quote.valid === true),
    requiresRequote: enabled(
      data.requires_requote ??
        data.requiresRequote ??
        quote.requires_requote ??
        quote.requiresRequote ??
        envelope.requires_requote ??
        envelope.requiresRequote,
    ),
    errors: normalizeShopperSafeErrors(
      data.errors,
      quote.errors,
      envelope.errors,
    ),
    quoteId: safeText(
      quote.quote_id || quote.quoteId || data.quote_id || data.quoteId,
      120,
    ) || null,
    contextFingerprint:
      safeText(
        quote.context_fingerprint ||
          quote.contextFingerprint ||
          contextFingerprint,
        80,
      ) || null,
    currency,
    displayCurrency,
    subtotal: quoteValue(quote, "subtotal", "subtotal", currency, fractionDigits),
    discountTotal: quoteValue(
      quote,
      "discount_total",
      "discountTotal",
      currency,
      fractionDigits,
    ),
    deliveryRate: quoteValue(
      quote,
      "delivery_rate",
      "deliveryRate",
      currency,
      fractionDigits,
    ),
    vatAmount: quoteValue(quote, "vat_amount", "vatAmount", currency, fractionDigits),
    additionalFee: quoteValue(
      quote,
      "additional_fee",
      "additionalFee",
      currency,
      fractionDigits,
    ),
    giftWrapping: quoteValue(
      quote,
      "gift_wrapping",
      "giftWrapping",
      currency,
      fractionDigits,
    ),
    orderTotal: quoteValue(
      quote,
      "order_total",
      "orderTotal",
      currency,
      fractionDigits,
    ),
    walletApplied: quoteValue(
      quote,
      "wallet_applied",
      "walletApplied",
      currency,
      fractionDigits,
    ),
    pointsApplied: quoteValue(
      quote,
      "points_applied",
      "pointsApplied",
      currency,
      fractionDigits,
    ),
    total: quoteValue(quote, "total", "total", currency, fractionDigits),
    minimumOrderRemaining: quoteValue(
      quote,
      "mov_remaining",
      "movRemaining",
      currency,
      fractionDigits,
    ),
    meetsMinimum:
      quote.meets_minimum === true || quote.meetsMinimum === true,
    paymentMethods: paymentMethodOptions
      .filter((method) => method.eligible)
      .map((method) => method.id),
    paymentMethodOptions,
    expiresAt: timestamp(quote.expires_at || quote.expiresAt),
    calculatedAt: now,
    version: safeText(quote.version, 40) || null,
    appliedPromotions: (Array.isArray(quote.applied_promotions)
      ? quote.applied_promotions
      : Array.isArray(quote.appliedPromotions)
        ? quote.appliedPromotions
        : []
    )
      .map((promotion) => ({
        id:
          promotion.promotion_id ??
          promotion.promotionId ??
          promotion.discount_id ??
          promotion.discountId ??
          null,
        kind: safeText(promotion.kind || promotion.type, 80) || null,
        code: safeText(promotion.code, 120) || null,
        name: safeText(promotion.name, 200) || null,
        amount: normalizeMoneyValue(
          promotion.money ?? promotion.amount,
          { currency, fractionDigits },
        ),
      }))
      .slice(0, 100),
  };
};

export const normalizePaymentStatusResponse = (response = {}) => {
  const { envelope, data } = unwrapEnvelope(response);
  const order = data.order || data.promise || envelope.order || data;
  const paymentStatus = normalizePaymentStatus(order);
  const orderStatus = normalizeTrackingStatus(
    order.status || data.order_status || data.orderStatus,
    {
      isPickup:
        order.is_delivery === false || order.isDelivery === false,
      isTable: enabled(order.is_table || order.isTable),
    },
  );
  const alternatives =
    data.alternative_methods ||
    data.alternativeMethods ||
    data.payment_methods ||
    [];
  return {
    success: envelope.success !== false && data.success !== false,
    trackingId: safeText(
      order.tracking_id || order.trackingId || data.tracking_id,
      120,
    ),
    paymentStatus,
    orderStatus,
    terminal:
      paymentStatus === "succeeded" ||
      paymentStatus === "failed" ||
      orderStatus.terminal,
    retryAllowed: enabled(data.retry_allowed ?? data.retryAllowed),
    retryReason: safeText(data.retry_reason || data.retryReason, 120) || null,
    alternativeMethods: (Array.isArray(alternatives) ? alternatives : [])
      .map((method) =>
        safeText(typeof method === "object" ? method.id : method, 80),
      )
      .filter(Boolean)
      .slice(0, 50),
    order,
  };
};

export const normalizePaymentRetryResponse = (response = {}) => {
  const { envelope, data } = unwrapEnvelope(response);
  const paymentUrl = safeText(
    data.payment_url ||
      data.paymentURL ||
      data.paymentUrl ||
      data.url ||
      envelope.payment_url ||
      envelope.paymentURL ||
      envelope.paymentUrl ||
      envelope.url,
    2000,
  );
  return {
    success:
      envelope.success !== false && data.success !== false && !!paymentUrl,
    paymentUrl: paymentUrl || null,
    trackingId:
      safeText(data.tracking_id || data.trackingId, 120) || null,
    retryAllowed: enabled(data.retry_allowed ?? data.retryAllowed),
  };
};

export const checkoutV2Api = {
  validateCartContext(payload, config) {
    return instance
      .post(
        CHECKOUT_V2_API_PATHS.validateCartContext,
        encodeCheckoutServerRequest(payload),
        config,
      )
      .then((response) => normalizeContextValidationResponse(response.data));
  },
  fetchCheckoutQuote(payload, config) {
    return instance
      .post(
        CHECKOUT_V2_API_PATHS.checkoutQuote,
        encodeCheckoutServerRequest(payload),
        config,
      )
      .then((response) =>
        normalizeCheckoutQuoteResponse(
          response.data,
          payload.context?.fingerprint,
        ),
      );
  },
  fetchDeliverySlots(payload, config) {
    return instance
      .post(CHECKOUT_V2_API_PATHS.deliveryTime, payload, config)
      .then((response) => response.data || {});
  },
  fetchPaymentStatus(trackingId, config) {
    return instance
      .get(CHECKOUT_V2_API_PATHS.paymentStatus(trackingId), config)
      .then((response) => normalizePaymentStatusResponse(response.data));
  },
  retryPayment(trackingId, payload = {}, config) {
    return instance
      .post(CHECKOUT_V2_API_PATHS.paymentRetry(trackingId), payload, config)
      .then((response) => normalizePaymentRetryResponse(response.data));
  },
  placeOrder(payload, config) {
    return instance
      .post(CHECKOUT_V2_API_PATHS.placeOrder, payload, config)
      .then((response) => response.data);
  },
  authenticateEmail(payload, config) {
    return instance
      .post(CHECKOUT_V2_API_PATHS.emailAuth, payload, config)
      .then((response) => response.data);
  },
  resetEmailPassword(payload, config) {
    return instance.post(CHECKOUT_V2_API_PATHS.resetEmailPassword, payload, config).then((response) => response.data);
  },
  resetPhonePassword(payload, config) {
    return instance.post(CHECKOUT_V2_API_PATHS.resetPhonePassword, payload, config).then((response) => response.data);
  },
  authenticatePhone(payload, config) {
    return instance
      .post(CHECKOUT_V2_API_PATHS.phoneAuth, payload, config)
      .then((response) => response.data);
  },
  verifyEmail(payload, config) {
    return instance
      .post(CHECKOUT_V2_API_PATHS.verifyEmail, payload, config)
      .then((response) => response.data);
  },
  verifyPhone(payload, config) {
    return instance
      .post(CHECKOUT_V2_API_PATHS.verifyPhone, payload, config)
      .then((response) => response.data);
  },
  fetchLegacyTracking(trackingId, countryCode, config) {
    return instance
      .post(
        CHECKOUT_V2_API_PATHS.legacyTracking(trackingId),
        { country_code: countryCode },
        config,
      )
      .then((response) => response.data);
  },
  async fetchPaymentStatusWithFallback(trackingId, countryCode, config) {
    try {
      return await this.fetchPaymentStatus(trackingId, config);
    } catch (error) {
      if (
        !error?.response ||
        ![404, 405, 501].includes(error.response.status)
      )
        throw error;
      const legacy = await this.fetchLegacyTracking(
        trackingId,
        countryCode,
        config,
      );
      return normalizePaymentStatusResponse(legacy?.data || legacy);
    }
  },
};
